import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import {
  getCrmConnections,
  connectAffinity,
  disconnectCrm,
  testCrmConnection,
  type CrmConnections,
  type CrmName,
  type CrmTestResult,
} from '../lib/crm'
import { isExtensionInstalled } from '../lib/extension'

/**
 * Reactive extension-installed probe. `isExtensionInstalled()` reads the
 * DOM once; the extension's content script appends the sentinel at
 * `run_at: document_start` but that can still arrive *after* a React hook
 * mounts (e.g. slow initial injection, extension installed mid-session).
 * Without this probe the query stays `enabled: false` forever and the user
 * has to reload to get CRM data.
 *
 * We watch documentElement for child additions and flip to true the moment
 * `#fwdslash-ext` appears. Once it's there, the observer disconnects.
 */
function useExtensionReady(): boolean {
  const [ready, setReady] = useState(() => isExtensionInstalled())
  useEffect(() => {
    if (ready) return
    if (typeof document === 'undefined') return
    const check = () => {
      if (isExtensionInstalled()) {
        setReady(true)
        return true
      }
      return false
    }
    if (check()) return
    const observer = new MutationObserver(() => {
      if (check()) observer.disconnect()
    })
    observer.observe(document.documentElement, { childList: true, subtree: false })
    return () => observer.disconnect()
  }, [ready])
  return ready
}

/**
 * Reference migration for the TanStack Query adoption.
 *
 * Before: ~95 lines of hand-rolled useState/useCallback + manual event wiring.
 * After:  ~75 lines using useQuery + useMutation.
 *
 * The public return shape is UNCHANGED so consumers don't need to migrate.
 * When you port other hooks, keep their public shape identical — that way the
 * migration is a one-file edit each time.
 *
 * Gains over the original:
 *   - Automatic deduplication if two components mount `useCrmConnections` at
 *     once, they share one request.
 *   - Automatic refetch on window focus (configured in queryClient.ts).
 *   - Cache survives unmount → remounting is instant from cache.
 *   - Errors + retries are consistent with every other query in the app.
 *   - Cross-tab sync via `CRM_CONNECTIONS_CHANGED` becomes a simple
 *     `queryClient.invalidateQueries`.
 */

export const crmConnectionsKey: QueryKey = ['crm', 'connections']

export function useCrmConnections() {
  const qc = useQueryClient()
  const extensionReady = useExtensionReady()

  const query = useQuery<CrmConnections>({
    queryKey: crmConnectionsKey,
    queryFn: getCrmConnections,
    // No extension = no data source. Skip the request entirely; UI branches
    // on `extensionReady` to prompt the install.
    enabled: extensionReady,
    placeholderData: { affinity: null },
  })

  // Cross-context invalidation: extension broadcasts when connections change
  // in another tab or from its own initiative. Replaces the manual listener +
  // setState dance in the old implementation.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // Same same-window/same-origin guard as extension.ts — an iframe could
      // otherwise force a refetch loop by spamming this event.
      if (event.source !== window) return
      if (event.origin !== window.location.origin) return
      if (event.data?.type === 'CRM_CONNECTIONS_CHANGED') {
        qc.invalidateQueries({ queryKey: crmConnectionsKey })
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [qc])

  const connectMutation = useMutation({
    mutationKey: ['crm', 'connect'],
    mutationFn: async ({ crm, apiKey }: { crm: CrmName; apiKey: string }) => {
      if (crm === 'affinity') return connectAffinity(apiKey)
      throw new Error(`Unsupported CRM: ${crm}`)
    },
    onSuccess: (connection, { crm }) => {
      // Optimistic cache update — avoids a refetch round-trip.
      qc.setQueryData<CrmConnections>(crmConnectionsKey, (prev) => ({
        ...(prev ?? { affinity: null }),
        [crm]: connection,
      }))
    },
  })

  const testMutation = useMutation({
    mutationKey: ['crm', 'test'],
    mutationFn: (crm: CrmName) => testCrmConnection(crm),
    onSuccess: (result, crm) => {
      if (!result.ok) {
        qc.invalidateQueries({ queryKey: crmConnectionsKey })
        return
      }
      qc.setQueryData<CrmConnections>(crmConnectionsKey, (prev) => {
        const existing = prev?.[crm]
        if (!existing) return prev
        return { ...prev, [crm]: { ...existing, status: 'active', verifiedAt: result.verifiedAt } }
      })
    },
  })

  const disconnectMutation = useMutation({
    mutationKey: ['crm', 'disconnect'],
    mutationFn: (crm: CrmName) => disconnectCrm(crm).then(() => crm),
    onSuccess: (crm) => {
      qc.setQueryData<CrmConnections>(crmConnectionsKey, (prev) => ({
        ...(prev ?? { affinity: null }),
        [crm]: null,
      }))
    },
  })

  return {
    connections: query.data ?? { affinity: null },
    loading: extensionReady ? query.isPending : false,
    error: query.error instanceof Error ? query.error.message : null,
    extensionReady,
    refresh: () => qc.invalidateQueries({ queryKey: crmConnectionsKey }),

    // Mutations keep the original "returns { ok, ... }" envelope so the UI
    // doesn't have to learn mutation.mutateAsync + try/catch.
    connect: async (crm: CrmName, apiKey: string) => {
      try {
        const connection = await connectMutation.mutateAsync({ crm, apiKey })
        return { ok: true as const, connection }
      } catch (e) {
        return { ok: false as const, error: e instanceof Error ? e.message : 'Connection failed' }
      }
    },
    test: async (crm: CrmName): Promise<CrmTestResult> => {
      try {
        return await testMutation.mutateAsync(crm)
      } catch (e) {
        return { ok: false, status: 'error', message: e instanceof Error ? e.message : 'Test failed' }
      }
    },
    disconnect: async (crm: CrmName) => {
      try {
        await disconnectMutation.mutateAsync(crm)
        return { ok: true as const }
      } catch (e) {
        return { ok: false as const, error: e instanceof Error ? e.message : 'Disconnect failed' }
      }
    },
  }
}
